import React, { useState } from 'react';
import { Calendar, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

// Helper to get auth headers including tenant site ID
const getHeaders = () => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    headers['x-site-id'] = activeSiteId;
  }
  return headers;
};

const TestEOD: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [testData, setTestData] = useState<any>(null);

  const checkTestData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/pos/test/data', {
        headers: getHeaders(),
      });
      
      const data = await response.json();
      setTestData(data);
      console.log('Test data:', data);
      
      if (response.ok) {
        toast.success('Test data loaded!');
      } else {
        toast.error(`Error: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Error checking test data:', error);
      toast.error('Failed to check test data');
    } finally {
      setLoading(false);
    }
  };

  const createTestOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/pos/test/create-orders', {
        method: 'POST',
        headers: getHeaders(),
      });
      
      const data = await response.json();
      setResult(data);
      console.log('Created test orders:', data);
      
      if (response.ok) {
        toast.success(`Created ${data.orders?.length || 0} test orders!`);
        // Refresh test data
        checkTestData();
      } else {
        toast.error(`Error: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Error creating test orders:', error);
      toast.error('Failed to create test orders');
    } finally {
      setLoading(false);
    }
  };

  const testEOD = async () => {
    setLoading(true);
    try {
      console.log('Testing EOD with date:', selectedDate);
      console.log('Headers:', getHeaders());
      
      const response = await fetch(`/api/pos/reports/end-of-day?date=${selectedDate}`, {
        headers: getHeaders(),
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      const data = await response.json();
      console.log('Response data:', data);
      
      setResult(data);
      
      if (response.ok) {
        toast.success('EOD API working!');
      } else {
        toast.error(`EOD API error: ${data.error}`);
      }
    } catch (error: any) {
      console.error('EOD test error:', error);
      toast.error('EOD test failed');
      setResult({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">EOD Report Test</h1>
        
        {/* Test Data Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Data Management</h2>
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={checkTestData}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Checking...' : 'Check Test Data'}
            </button>
            <button
              onClick={createTestOrders}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Creating...' : 'Create Test Orders'}
            </button>
          </div>
          
          {testData && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Current Data:</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Customers:</span> {testData.customers}
                </div>
                <div>
                  <span className="font-medium">Services:</span> {testData.services}
                </div>
                <div>
                  <span className="font-medium">Orders:</span> {testData.orders}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Site ID: {testData.siteId}</p>
            </div>
          )}
        </div>
        
        {/* EOD Test Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">EOD Report Test</h2>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
            <button
              onClick={testEOD}
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Testing...' : 'Test EOD API'}
            </button>
          </div>
          
          <div className="text-sm text-gray-600">
            <p>Active Site ID: {localStorage.getItem('activeSiteId') || 'Not set'}</p>
            <p>Token: {localStorage.getItem('token') ? 'Set' : 'Not set'}</p>
          </div>
        </div>
        
        {result && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Result</h2>
            <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm">
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestEOD;
